import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, CONSENT_VERSION } from '@neke/core';
import type { ApiError } from '../api/client';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { routeOf, track } from '../analytics';

/**
 * Google отдаёт только почту и имя. Пол и согласие на обработку данных
 * спрашиваем здесь — без них анкету не завершить и в подбор не попасть.
 */
export function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser } = useSession();

  const [sex, setSex] = useState<'M' | 'F' | null>(user?.sex ?? null);
  const [consent, setConsent] = useState(user?.hasConsent ?? false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = params.get('next') ?? '/test';

  const submit = () => {
    if (!sex || !consent) return;
    setBusy(true);
    void api
      .updateMe({ sex })
      .then((u) => {
        setUser(u);
        return user?.hasConsent ? u : api.acceptConsent(CONSENT_VERSION);
      })
      .then((u) => {
        setUser(u);
        void track(ANALYTICS_EVENTS.onboardingCompleted, { sex, next_route: routeOf(next) });
        navigate(next);
      })
      .catch((e: ApiError) => {
        setError(e.message);
        void track(ANALYTICS_EVENTS.onboardingFailed, { code: e.code });
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="page">
      <h1>{t('onboarding.title')}</h1>
      <p className="muted">{t('onboarding.lead')}</p>

      <div className="card">
        <label className="field">
          <span>{t('auth.sex')}</span>
          <div className="btn-row">
            <button
              className={sex === 'M' ? 'btn btn-primary' : 'btn btn-ghost'}
              onClick={() => setSex('M')}
            >
              {t('auth.male')}
            </button>
            <button
              className={sex === 'F' ? 'btn btn-primary' : 'btn btn-ghost'}
              onClick={() => setSex('F')}
            >
              {t('auth.female')}
            </button>
          </div>
        </label>
        <p className="fine">{t('onboarding.sexHint')}</p>
      </div>

      <div className="card">
        <label className="check">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span className="fine">{t('onboarding.consent')}</span>
        </label>
      </div>

      <button className="btn btn-primary" onClick={submit} disabled={!sex || !consent || busy}>
        {busy ? t('common.loading') : t('onboarding.submit')}
      </button>

      {error && (
        <p className="fine error">
          {error}
        </p>
      )}
    </div>
  );
}
